import React, { useContext, useEffect, useState } from 'react';
import {
  WithSitecoreContextProps,
  withSitecoreContext,
  Field,
  RichText,
} from '@sitecore-jss/sitecore-jss-react';
import ToastNotification from '../ToastNotification/ToastNotification';
import { Button, Modal } from '@mui/material';
import { Navigate } from 'react-router-dom'; // Change Redirect to Navigate
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import LoadingSection from '../LoadingSection/LoadingSection';
import { SubmitProcessValidationService } from './SubmitProcessValidationService';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import PaymentErrors from '../PlaceOrderProcess/PlaceOrderProcess';
import { getIdpToken } from '../../utils/authHelper';
import { UnifiedPaymentDataService } from '../../utils/UnifiedPaymentDataService';
import { OrderAnalyticsInfo, purchaseEvent } from '../../utils/analyticsHelper';
import { PaymentTypes } from '../../utils/PaymentTypes';
import './index.scss';
import {
  PAYMENT_TYPES,
  STATUS_OK,
  STATUS_REDIRECT,
  VERIFONE_PAYMENT_IDENTIFIER,
} from '../../utils/constants';
import ProcessOrderValidationService from '../CheckoutPage/ProcessOrderValidationService';
import { DialogActions, DialogContent, DialogTitle } from '../DialogComponent/DialogComponent';

export interface ErrorResponse {
  errorCode: string;
  errorMessage: string;
}

export interface IPayment {
  AccountNumber: string;
  CardType: string;
  ExpirationDate: string;
  CVV: string;
  CardholderName: string;
}

export interface INonInstantPayment {
  bankName?: string;
  localBankName?: string;
  usBankName?: string;
  checkOrMoneyOrderNumber?: string;
  remitterName?: string;
  paymentDocumentsName?: string[];
}

export interface OrderResponse {
  error: ErrorResponse;
  orderId: string;
  responseStatus: string;
}

type SubmitPaymentProcessProps = {
  fields: {
    ToastMessage: Field<string>;
    ToastMessagePayFabric: Field<string>;
    PaymentAttemptText: Field<string>;
  };
  isEULA: boolean;
  isOrderLoading: boolean;
  orderTotal?: number;
  orderIds?: string[];
  invoiceIdentifier: string;
  submitButtonText: string;
  language: string;
};

export type OrderPaymentRequest = {
  orderId: string;
  PaymentMethod: string;
};

const SubmitPaymentProcess = (
  props: SubmitPaymentProcessProps & WithSitecoreContextProps
): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [navigateOnOrderSubmit, setNavigateOnOrderSubmit] = useState<{
    pathname: string;
    search?: string;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [customToastMessage] = useState(props.fields.ToastMessage);
  const [orderPaymentRequest, setOrderPaymentRequest] = useState<OrderPaymentRequest[]>([]);

  const groupNumber = state?.adminGroupNumber ?? '';
  const groupInvoiceId = state?.groupInvoiceId?.[props?.invoiceIdentifier] ?? '';
  const groupNumberInvoiceId = state?.groupNumberInvoiceId?.[props?.invoiceIdentifier] ?? '';
  const paymentType = state?.invoicePayment?.[props?.invoiceIdentifier];
  const [paymentConfirmationPopup, setPaymentConfirmationPopup] = useState(false);

  function replacePaymentAttemptText(): Field<string> {
    return {
      value:
        props?.fields?.PaymentAttemptText?.value?.replace(
          '##RemainingAttempts##',
          String(state?.remainingPaymentAttempts)
        ) || '',
    };
  }

  function renderPaymentAttemptPopup() {
    return (
      <Modal
        open={paymentConfirmationPopup}
        onClose={(_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
          if (reason !== 'backdropClick') setPaymentConfirmationPopup(false);
        }}
        className="ws-modal-dialog"
      >
        <div className="confirmationDialog confirmationDialogContent">
          <DialogTitle
            id="customized-dialog-title"
            onClose={() => setPaymentConfirmationPopup(false)}
          >
            Payment Confirmation
          </DialogTitle>
          <DialogContent dividers>
            <RichText field={replacePaymentAttemptText()} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentConfirmationPopup(false)} className="ws-white-button">
              Cancel
            </Button>
            <Button
              onClick={() => {
                setPaymentConfirmationPopup(false);
                processOrder();
              }}
              className="ws-primary-button-no-border"
            >
              Proceed
            </Button>
          </DialogActions>
        </div>
      </Modal>
    );
  }

  function formJSONAndPushToGTM() {
    try {
      const cartData = UnifiedPaymentDataService.getPaymentInfo();
      const lob = props?.invoiceIdentifier;
      const cartInfo = cartData?.[lob]?.cartInfo;
      const unpaidInvoices: Array<any> = cartInfo?.unpaidInvoices ?? [];

      const invoiceSummary = UnifiedPaymentDataService.getInvoiceSummary()?.[lob];

      const tax = invoiceSummary?.taxTotal?.toFixed(2) ?? '0.00';
      const shipping = invoiceSummary?.shippingTotal?.toFixed(2) ?? '0.00';

      unpaidInvoices.forEach((invoice: any) => {
        const orderId = invoice?.orderId;
        let value: any = invoice?.totalAmount;
        value = value.toFixed(2);

        const orderAnalyticsInfo: OrderAnalyticsInfo = {
          transaction_id: orderId,
          tax: tax,
          shipping: shipping,
          value: value,
          currency: 'USD',
          payment_type: state?.invoicePayment?.[lob] ?? '',
          items: invoice.products.map((product: any) => {
            let price: any = product?.price;
            price = price.toFixed(2);
            return {
              item_id: `${product?.productId}`,
              item_name: `${product?.productName}`,
              item_variant: product?.type || undefined,
              price: price,
              quantity: product?.quantity,
            };
          }),
        };

        purchaseEvent(orderAnalyticsInfo);
      });
    } catch (error: any) {
      console.error('GTM push error:', error);
    }
  }

  const toastRemove = () => {
    setShowToast(false);
  };

  function submitPaymentProcessStatus(isLoading: boolean) {
    if (isLoading) {
      return (
        <LoadingSection
          open={isLoading}
          message="Please do not go back or close the window or hit the button again"
        />
      );
    } else {
      return <div></div>;
    }
  }

  const showCustomToast = (message: string) => {
    customToastMessage.value = message;
    setToastMessage(customToastMessage);
    setShowToast(true);
  };

  function hasBillingAddressErrors(billAddress: any) {
    if (
      billAddress.country.length < 2 ||
      billAddress.city === '' ||
      billAddress.firstName === '' ||
      billAddress.lastName === '' ||
      billAddress.postal === '' ||
      billAddress.state === '' ||
      billAddress.street === ''
    ) {
      return state?.sitecoreDictionaryData
        ? state?.sitecoreDictionaryData.BillingAddressErrorMessage
        : 'Billing Address Error';
    } else {
      return '';
    }
  }

  async function processOrder() {
    if (!ProcessOrderValidationService.getIsBillingAddressSaved().isBillingSaved) {
      customToastMessage.value =
        state?.sitecoreDictionaryData?.BillingAddressError ??
        'Please enter and save the billing details';
      setToastMessage(customToastMessage);
      setShowToast(true);
      return;
    }

    const OrderRequest: OrderPaymentRequest[] = (props.orderIds || []).map((orderId) => ({
      orderId,
      PaymentMethod: state?.invoicePayment?.[props.invoiceIdentifier] || '',
    }));

    setOrderPaymentRequest(OrderRequest);

    if (isDisabled) {
      return;
    }

    setIsDisabled(true);

    setTimeout(() => {
      setIsDisabled(false);
    }, 2000);

    if (paymentType) {
      if (dispatch) {
        dispatch({
          type: 'INVOICE_UPDATE',
          payload: {
            toggleSubmitPaymentValidation: !state?.toggleSubmitPaymentValidation,
          },
        });
      }
    }

    if (
      (!state?.invoicePayment?.[props?.invoiceIdentifier] ||
        state?.invoicePayment?.[props?.invoiceIdentifier].length === 0) &&
      state?.groupAdminCountry !== 'CN'
    ) {
      showCustomToast(
        state?.sitecoreDictionaryData?.InvalidPaymentMethod ?? 'Please select a payment method'
      );
      setIsDisabled(false);
      return;
    }

    if (!props.isEULA) {
      showCustomToast(
        state?.sitecoreDictionaryData?.EULAErrorMessage ??
          'Please read and accept the EULA to proceed with checkout.'
      );
      setIsDisabled(false);
      return;
    }

    try {
      if ((props?.orderTotal ?? 0) > 0 && state?.groupAdminCountry === 'CN') {
        const billingService = SubmitProcessValidationService.getBillingInfo();

        if (billingService.billingInfo.toString() !== '[object Object]') {
          const billingInfo = billingService.billingInfo();

          if (!billingInfo || Object.keys(billingInfo).length === 0) {
            showCustomToast(
              state?.sitecoreDictionaryData?.BillingAddressError ??
                'Please enter and save the billing details'
            );
            setIsDisabled(false);
            return;
          }

          const billingErrors = hasBillingAddressErrors(billingInfo);
          if (billingErrors.length > 0) {
            showCustomToast(billingErrors);
            setIsDisabled(false);
            return;
          }
        }

        setIsLoading(true);
        await redirectForExternalPaymentProcessing(OrderRequest);
        return;
      }
    } catch (error: any) {
      showCustomToast(
        state?.sitecoreDictionaryData?.CheckoutExternalPaymentRedirectCatchError ??
          'Order Processing Failed. Please retry.'
      );
      setIsLoading(false);
      setIsDisabled(false);
      return;
    }

    setTimeout(() => {
      const billingService = SubmitProcessValidationService.getBillingInfo();

      if (billingService.billingInfo.toString() !== '[object Object]') {
        const billingInfo = billingService.billingInfo();

        if (billingInfo && Object.keys(billingInfo).length > 0) {
          const billingErrors = hasBillingAddressErrors(billingInfo);
          if (billingErrors.length > 0) {
            showCustomToast(billingErrors);
            setIsDisabled(false);
            return;
          }
        }
      }

      const errorMessageText = SubmitProcessValidationService.getErrorMessage().text;
      if (errorMessageText && errorMessageText !== '') {
        showCustomToast(errorMessageText);
        setIsDisabled(false);
        return;
      }

      if (paymentType !== PaymentTypes.CreditCard) {
        const nonInstantData = SubmitProcessValidationService.getNonInstantPaymentInfo();
        if (!nonInstantData?.isValid) {
          showCustomToast(
            state?.sitecoreDictionaryData?.InvalidPaymentDetailsToastMessage ??
              'Please fill in all required non-instant payment fields correctly.'
          );
          setIsDisabled(false);
          return;
        }
      }

      submitOrder(OrderRequest, groupNumber, groupNumberInvoiceId);
    }, 50);
  }

  function findDebitCardType(ccNumber: string) {
    const regexPattern: { [key: string]: RegExp } = {
      MASTER_CARD: /^5[1-5][0-9]{1,}|^2[2-7][0-9]{1,}$/,
      VISA: /^4[0-9]{2,}$/,
      AMERICAN_EXPRESS: /^3[47][0-9]{5,}$/,
      UNION_PAY: /^(62[0-9]{14,17})$/,
    };

    for (const item in regexPattern) {
      if (ccNumber?.replace(/[^\d]/g, '').match(regexPattern[item])) {
        return item;
      }
    }
    return '';
  }

  function submitOrder(
    orderRequest: OrderPaymentRequest[],
    groupNumber: string,
    groupNumberInvoiceId: string
  ) {
    let billingInfo = null;
    let payment: IPayment | null = null;
    let purchaseOrderPaymentDetails: INonInstantPayment | null = null;

    let billData = ProcessOrderValidationService.getBillingInfo().billingInfo();
    billingInfo = {
      FirstName: billData.firstName,
      LastName: billData.lastName,
      Street1: billData.street,
      Street2: billData.unit,
      City: billData.city,
      State: billData.state,
      Zip: billData.postal,
      Country: billData.country,
      Phone: billData.phone,
    };

    if (paymentType === PaymentTypes.CreditCard) {
      let ccInfoData = SubmitProcessValidationService.getCCInfo();
      let cardType = findDebitCardType(ccInfoData.ccinfo.card);
      payment = {
        AccountNumber: ccInfoData.ccinfo.card,
        CardType: cardType,
        ExpirationDate: ccInfoData.ccinfo.expiry,
        CVV: ccInfoData.ccinfo.securityCode,
        CardholderName: ccInfoData.ccinfo.cardHolder,
      };
    } else {
      const nonInstantPayment: INonInstantPayment =
        SubmitProcessValidationService.getNonInstantPaymentInfo().paymentInfo;

      purchaseOrderPaymentDetails = {
        bankName: nonInstantPayment.bankName,
        localBankName: nonInstantPayment.localBankName,
        usBankName: nonInstantPayment.usBankName,
        checkOrMoneyOrderNumber: nonInstantPayment.checkOrMoneyOrderNumber,
        remitterName: nonInstantPayment.remitterName,
        paymentDocumentsName: nonInstantPayment.paymentDocumentsName,
      };
    }
    let submitOrderRequest = {
      orders: orderRequest,
      Payment: payment,
      PurchaseOrderPaymentDetails: purchaseOrderPaymentDetails,
      BillingAddress: billingInfo,
      GroupNumber: groupNumber,
      GroupInvoiceId: groupInvoiceId,
      GroupNumberInvoiceId: groupNumberInvoiceId,
      isGroupInvoice: true,
    };

    const idpToken = getIdpToken();
    setIsLoading(true);

    postAPICall(
      `${process.env.REACT_APP_CART_URL}/checkout/submitcustominvoice`,
      submitOrderRequest,
      {
        headers: {
          'B2C-Authorization': idpToken,
        },
      }
    )
      .then(function (response: any) {
        if (response && response.status === 200) {
          processOrderResponse(response?.data);

          // Push to GTM after successful order
          formJSONAndPushToGTM();
        }
      })
      .catch(function (error) {
        if (error?.response?.data) {
          processOrderResponse(error?.response?.data);
        }
      });
  }

  function processOrderResponse(orderResponse: OrderResponse) {
    if (orderResponse) {
      if (dispatch) {
        var toggleOrderSummaryLoadValue =
          orderResponse.responseStatus === 'Confirmed' || orderResponse.responseStatus === 'Pending'
            ? state?.toggleOrderSummaryLoad
            : !state?.toggleOrderSummaryLoad;
        dispatch({
          type: 'CART_UPDATE',
          payload: {
            toggleCartQuantityLoad: !state?.toggleCartQuantityLoad,
            lastOrderId: orderResponse.orderId,
            toggleOrderSummaryLoad: toggleOrderSummaryLoadValue,
          },
        });
      }

      switch (orderResponse.responseStatus) {
        case 'Confirmed':
          setNavigateOnOrderSubmit({
            pathname: '/payment/confirmation',
            search: `?invoiceNumber=${groupInvoiceId}`,
          });
          break;

        case 'Pending':
          setNavigateOnOrderSubmit({
            pathname: '/payment/pending',
            search: `?invoiceNumber=${groupInvoiceId}&paymentType=${
              PAYMENT_TYPES[paymentType ?? '']
            }`,
          });
          break;

        case 'Failed':
          if (orderResponse.error && orderResponse.error.errorCode === 'ServerError') {
            setNavigateOnOrderSubmit({
              pathname: '/payment/unsuccessful',
            });
          } else if (
            orderResponse &&
            orderResponse.error &&
            orderResponse.error.errorCode === 'OrderSubmit.CCPaymentAlreadyPaid'
          ) {
            showCustomToast(
              state?.sitecoreDictionaryData
                ? state?.sitecoreDictionaryData.CCPaymentAlreadyPaid
                : 'Unsuccessful Order processing. Payment already submitted using Credit Card. Please choose the same payment method or contact support for further assistance.'
            );
          } else if (orderResponse && orderResponse.error && orderResponse.error.errorCode !== '') {
            let errorKey = Object.keys(PaymentErrors).findIndex(
              (key) => key === orderResponse.error.errorMessage
            );
            let errorMessage =
              errorKey >= 0
                ? Object.values(PaymentErrors).at(errorKey)
                : orderResponse.error.errorMessage;
            showCustomToast(
              errorMessage || 'Unsuccessful Order processing. Please verify the details!'
            );
          } else {
            showCustomToast(
              state?.sitecoreDictionaryData
                ? state?.sitecoreDictionaryData.OrderUnsuccessfulRetryMessage
                : 'Unsuccessful Order processing. Please verify the details!'
            );
          }
          if (dispatch) {
            dispatch({
              type: 'CART_UPDATE',
              payload: {
                togglePaymentResponse: !state?.togglePaymentResponse,
              },
            });
          }

          setIsLoading(false);
          break;
        default:
          showCustomToast(
            state?.sitecoreDictionaryData
              ? state?.sitecoreDictionaryData.OrderUnsuccessfulRetryMessage
              : 'Unsuccessful Order processing. Please verify the details!'
          );
          setIsLoading(false);
      }
    }
  }

  async function redirectForExternalPaymentProcessing(orderRequest: OrderPaymentRequest[]) {
    orderRequest.forEach((order) => {
      order.PaymentMethod = VERIFONE_PAYMENT_IDENTIFIER;
    });

    const idpToken = getIdpToken();

    let billData = ProcessOrderValidationService.getBillingInfo().billingInfo();
    let billingInfo = {
      FirstName: billData.firstName,
      LastName: billData.lastName,
      Street1: billData.street,
      Street2: billData.unit,
      City: billData.city,
      State: billData.state,
      Zip: billData.postal,
      Country: billData.country,
      Phone: billData.phone,
    };

    let submitOrderRequest = {
      orders: orderRequest,
      BillingAddress: billingInfo,
      GroupNumber: groupNumber,
      GroupInvoiceId: groupInvoiceId,
      GroupNumberInvoiceId: groupNumberInvoiceId,
      isGroupInvoice: true,
      country: state?.groupAdminCountry,
      language: props?.language,
    };

    const response = await postAPICall(
      `${process.env.REACT_APP_CART_URL}/external/generateunifiedbuylink`,
      submitOrderRequest,
      {
        headers: {
          'B2C-Authorization': idpToken,
        },
      }
    );

    if (
      response &&
      Number(response.status) >= STATUS_OK &&
      Number(response.status) < STATUS_REDIRECT
    ) {
      window.location.assign(response.headers.location);
    }
  }

  return (
    <div className="submitPaymentProcess">
      {props.sitecoreContext.pageEditing && (
        <>
          <p>SubmitPaymentProcess Component Edit Mode</p>
        </>
      )}

      <div className="submitPaymentButton">
        {props.isOrderLoading ? (
          <Button color="secondary" variant="contained" className="checkoutButton">
            <LoadingIcon />
          </Button>
        ) : (
          <Button
            color="secondary"
            variant="contained"
            className="checkoutButton"
            onClick={() => {
              if (paymentType === PaymentTypes.CreditCard && state?.remainingPaymentAttempts) {
                setPaymentConfirmationPopup(true);
              } else {
                processOrder();
              }
            }}
          >
            <span className="checkoutButtonText">{props?.submitButtonText}</span>
          </Button>
        )}
      </div>

      <div>{submitPaymentProcessStatus(isLoading)}</div>
      {navigateOnOrderSubmit ? <Navigate to={navigateOnOrderSubmit} /> : null}
      <div>{renderPaymentAttemptPopup()}</div>

      <ToastNotification
        fields={{
          ToastMessage: toastMessage,
        }}
        showToast={showToast}
        removeToast={toastRemove}
        toastStatus="error"
      ></ToastNotification>
    </div>
  );
};

export default withSitecoreContext()(SubmitPaymentProcess);
